import { CustomerRegistrationFormData, ExtendedCustomerRegistrationErrors } from 'src/types/sign-up';

export const validateFields = (
  values: CustomerRegistrationFormData,
  t: (key: string, options?: object) => string,
  section: 'user' | 'storeDetails' | 'storeContact' | 'companyDetails' | 'companyContact' | 'invoiceDetails' | 'invoiceContact' | null = null
): ExtendedCustomerRegistrationErrors => {
  // Initialize the errors object with our new structure.
  const errors: ExtendedCustomerRegistrationErrors = {
    user: {
      email: '',
      password: '',
    },
    storeDetails: {
      line_1: '',
      line_2: '',
      zipCode: '',
      country: '',
      state: '',
      city: '',
    },
    storeContact: {
      storeName: '',
      attention: '',
      shippingEmail: '',
      shippingPhone: '',
      customerType: '',
    },
    companyDetails: {
      name: '',
      billingLine1: '',
      billingLine2: '',
      billingCity: '',
      billingZipCode: '',
      billingCountry: '',
      billingState: '',
      VATNumber: '',
      corporateID: '',
      applicantComment: '',
    },
    companyContact: {
      billingAttention: '',
      billingEmail: '',
      billingPhone: '',
    },
    invoiceDetails: {
      invoiceComapnyName: '',
      invoiceLine1: '',
      invoiceLine2: '',
      invoiceZipCode: '',
      invoiceCity: '',
      invoiceCountry: '',
      invoiceState: '',
    },
    invoiceContact: {
      invoiceAttention: '',
      invoiceEmail: '',
      invoicePhone: '',
    },
  };

  // ─── Validate USER SECTION ──────────────────────────────
  if (section === 'user' || section === null) {
    if (!values.email || !/\S+@\S+\.\S+/.test(values.email)) {
      errors.user.email = t('error.invalidEmail');
    }
    // Optionally, validate password if required.
  }

  // ─── Validate STORE DETAILS ─────────────────────────────
  if (section === 'storeDetails' || section === null) {
    if (!values.line_1) {
      errors.storeDetails.line_1 = t('error.addressLine1Required');
    }
    // line_2 may be optional.
    if (!values.zipCode) {
      errors.storeDetails.zipCode = t('error.zipCodeRequired');
    }
    if (!values.country || !values.country.value) {
      errors.storeDetails.country = t('error.countryRequired');
    }
    if (!values.city) {
      errors.storeDetails.city = t('error.cityRequired');
    }
  }

  // ─── Validate STORE CONTACT ─────────────────────────────
  if (section === 'storeContact' || section === null) {
    if (!values.storeName) {
      errors.storeContact.storeName = t('error.storeNameRequired');
    }
    if (!values.attention) {
      errors.storeContact.attention = t('error.attentionRequired');
    }
    if (!values.shippingEmail || !/\S+@\S+\.\S+/.test(values.shippingEmail)) {
      errors.storeContact.shippingEmail = t('error.invalidShippingEmail');
    }
    if (!values.shippingPhone || !/^[\d\s-]+$/.test(values.shippingPhone)) {
      errors.storeContact.shippingPhone = t('error.invalidShippingPhone');
    }
    if (!values.customerType || !values.customerType.value) {
      errors.storeContact.customerType = t('error.customerTypeRequired');
    }
  }

  // ─── Validate COMPANY DETAILS ─────────────────────────────
  if (section === 'companyDetails' || section === null) {
    if (!values.name) {
      errors.companyDetails.name = t('error.companyNameRequired');
    }
    if (!values.billingLine1) {
      errors.companyDetails.billingLine1 = t('error.billingAddressLine1Required');
    }
    // billingLine2 can be optional.
    if (!values.billingZipCode) {
      errors.companyDetails.billingZipCode = t('error.billingZipCodeRequired');
    }
    if (!values.billingCountry || !values.billingCountry.value) {
      errors.companyDetails.billingCountry = t('error.billingCountryRequired');
    }
    if (!values.billingCity) {
      errors.companyDetails.billingCity = t('error.billingCityRequired');
    }
    if (!values.VATNumber) {
      errors.companyDetails.VATNumber = t('error.vatNumberRequired');
    }
    if (!values.corporateID) {
      errors.companyDetails.corporateID = t('error.corporateIDRequired');
    }
  }

  // ─── Validate COMPANY CONTACT ─────────────────────────────
  if (section === 'companyContact' || section === null) {
    if (!values.billingEmail || !/\S+@\S+\.\S+/.test(values.billingEmail)) {
      errors.companyContact.billingEmail = t('error.invalidBillingEmail');
    }
    if (!values.billingPhone || !/^[\d\s-]+$/.test(values.billingPhone)) {
      errors.companyContact.billingPhone = t('error.invalidBillingPhone');
    }
    if (!values.billingAttention) {
      errors.companyContact.billingAttention = t('error.billingAttentionRequired');
    }
  }

  // ─── Validate INVOICE DETAILS ─────────────────────
  if (section === 'invoiceDetails' || section === null) {
    // Uncomment and use if invoice company name is required.
    // if (!values.invoiceComapnyName) {
    //   errors.invoiceDetails.invoiceComapnyName = t('error.invoiceCompanyNameRequired');
    // }
    if (!values.invoiceLine1) {
      errors.invoiceDetails.invoiceLine1 = t('error.invoiceAddressLine1Required');
    }
    // invoiceLine2 may be optional.
    if (!values.invoiceZipCode) {
      errors.invoiceDetails.invoiceZipCode = t('error.invoiceZipCodeRequired');
    }
    if (!values.invoiceCountry || !values.invoiceCountry.value) {
      errors.invoiceDetails.invoiceCountry = t('error.invoiceCountryRequired');
    }
    if (!values.invoiceCity) {
      errors.invoiceDetails.invoiceCity = t('error.invoiceCityRequired');
    }
  }

  // ─── Validate INVOICE CONTACT ─────────────────────
  if (section === 'invoiceContact' || section === null) {
    if (!values.invoiceAttention) {
      errors.invoiceContact.invoiceAttention = t('error.invoiceAttentionRequired');
    }
    if (!values.invoiceEmail || !/\S+@\S+\.\S+/.test(values.invoiceEmail)) {
      errors.invoiceContact.invoiceEmail = t('error.invalidInvoiceEmail');
    }
    if (!values.invoicePhone || !/^[\d\s-]+$/.test(values.invoicePhone)) {
      errors.invoiceContact.invoicePhone = t('error.invalidInvoicePhone');
    }
  }

  return errors;
};

export const hasAnyErrorMessage = (
  errors: ExtendedCustomerRegistrationErrors
): boolean => {
  return Object.values(errors).some(section =>
    Object.values(section).some(msg => msg.trim() !== '')
  );
};

export const hasErrorsInSection = (
  errors: ExtendedCustomerRegistrationErrors,
  section: keyof ExtendedCustomerRegistrationErrors
): boolean => {
  const sectionErrors = errors[section];
  return Object.values(sectionErrors)?.some(msg => msg.trim() !== '');
};
